import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import styles from '../tables.module.css';
import {PRISMAX_BACKEND_URL} from "../../../configs/config";
import {SwapMainAppContext} from '../../../context/SwapMainAppContext';
import {getApiErrorMessage, isSessionExpiredError} from '../../../utils/authErrors';

const PRIMARY_KEY = 'task_id';
const PREVIEW_VIDEO_URL_COLUMN = 'preview_video_url';
const PREVIEW_VIDEO_UPLOAD_ID_FIELD = 'preview_video_upload_id';
const DATA_FORMAT_COLUMN = 'data_format';
const CREATED_AT_COLUMN = 'created_at';
const DEFAULT_DATA_FORMAT = 'mcap/mp4';
const IS_VISIBLE_BETA_COLUMN = 'is_visible_beta';
const IS_VISIBLE_PROD_COLUMN = 'is_visible_production';
const VISIBILITY_COLUMNS = new Set([IS_VISIBLE_BETA_COLUMN, IS_VISIBLE_PROD_COLUMN]);

const LIST_FIELD_COLUMNS = new Set(['notes', 'task_variations', 'general_variations']);
const TAG_FIELD_COLUMNS = new Set(['objects']);
const PROSE_FIELD_COLUMNS = new Set(['details_description']);
const EXPANDED_CELL_COLUMNS = new Set([...LIST_FIELD_COLUMNS, ...TAG_FIELD_COLUMNS, ...PROSE_FIELD_COLUMNS]);

const ITEMS_PER_PAGE = 10;

function parseListField(value) {
    const text = String(value || '').trim();
    if (!text) return [];
    return text
        .split('\n')
        .flatMap((line) => {
            const trimmed = line.trim();
            if (!trimmed) return [];
            return [trimmed.replace(/^-\s*/, '')];
        })
        .filter(Boolean);
}

function parseTagField(value) {
    const text = String(value || '').trim();
    if (!text) return [];
    return text
        .split(/,(?![^(]*\))/g)
        .map((s) => s.trim())
        .map((s) => `${s.charAt(0).toUpperCase()}${s.substring(1)}`)
        .filter(Boolean);
}

const toLabel = (value) => String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const isExpandedColumn = (column) => EXPANDED_CELL_COLUMNS.has(column);

const renderExpandedCell = (column, value) => {
    const str = (value === null || value === undefined || value === '') ? null : String(value);
    if (!str) return <span className={styles.muted}>-</span>;

    if (TAG_FIELD_COLUMNS.has(column)) {
        const tags = parseTagField(str);
        if (!tags.length) return <span className={styles.muted}>-</span>;
        return (
            <ul className={styles.taskCellList}>
                {tags.map((tag, i) => (
                    <li key={i} className={styles.taskCellListItem}>{tag}</li>
                ))}
            </ul>
        );
    }

    if (LIST_FIELD_COLUMNS.has(column)) {
        const items = parseListField(str);
        if (!items.length) return <span className={styles.muted}>-</span>;
        return (
            <ul className={styles.taskCellList}>
                {items.map((item, i) => (
                    <li key={i} className={styles.taskCellListItem}>{item}</li>
                ))}
            </ul>
        );
    }

    return <span className={styles.taskCellText}>{str}</span>;
};

const shouldUseTextarea = (column) => /(description|details|notes|objects|variations)/i.test(String(column || ''));

const normalizeColumns = (rawColumns) => {
    const deduped = [];
    const seen = new Set();
    (rawColumns || []).forEach((column) => {
        const key = String(column || '').trim();
        if (!key || seen.has(key)) return;
        seen.add(key);
        deduped.push(key);
    });
    if (!seen.has(PRIMARY_KEY)) {
        deduped.unshift(PRIMARY_KEY);
        return deduped;
    }
    return [PRIMARY_KEY, ...deduped.filter((column) => column !== PRIMARY_KEY)];
};

const buildRowByColumns = (columns, source = {}) => {
    const row = {};
    columns.forEach((column) => {
        const value = source[column];
        row[column] = value === null || value === undefined ? '' : String(value);
    });
    return row;
};

const normalizeColumnDetails = (rawColumnDetails) => {
    const normalized = {};
    Object.entries(rawColumnDetails || {}).forEach(([column, detail]) => {
        normalized[column] = {
            input_type: detail?.input_type || 'text',
            data_type: detail?.data_type || 'text',
            required: Boolean(detail && detail.required),
        };
    });
    return normalized;
};

const formatInputTypeLabel = (detail) => {
    const value = detail?.input_type || detail?.data_type || 'text';
    return String(value).charAt(0).toUpperCase() + String(value).slice(1);
};

const getColumnHint = (column, detail) => {
    const parts = [];
    parts.push(detail?.required ? 'Required' : 'Optional');
    parts.push(`Type: ${formatInputTypeLabel(detail)}`);
    if (column === PRIMARY_KEY) parts.push('Read only');
    if (column === DATA_FORMAT_COLUMN) parts.push(`Default: ${DEFAULT_DATA_FORMAT}`);
    return parts.join(' • ');
};

const getInputMode = (detail) => {
    if (detail?.input_type === 'integer' || detail?.input_type === 'number') return 'numeric';
    return undefined;
};

const validateValueByType = (column, value, detail) => {
    const trimmedValue = String(value ?? '').trim();
    const label = toLabel(column);

    if (!trimmedValue) {
        if (detail?.required) return `${label} is required`;
        return '';
    }
    if (detail?.input_type === 'integer' && !/^[+-]?\d+$/.test(trimmedValue)) {
        return `${label} must be an integer`;
    }
    if (detail?.input_type === 'number' && Number.isNaN(Number(trimmedValue))) {
        return `${label} must be a number`;
    }
    if (detail?.input_type === 'boolean' && !/^(true|false|1|0|yes|no|y|n)$/i.test(trimmedValue)) {
        return `${label} must be true or false`;
    }
    return '';
};

const VLAAdminTasksTable = () => {
    const {handleAdminSessionExpired} = useContext(SwapMainAppContext);
    const [rows, setRows] = useState([]);
    const [columns, setColumns] = useState([]);
    const [columnDetails, setColumnDetails] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingTaskId, setEditingTaskId] = useState(null);
    const [formRow, setFormRow] = useState({});
    const [saveError, setSaveError] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [previewVideoUploadId, setPreviewVideoUploadId] = useState('');
    const [togglingId, setTogglingId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const isInitialLoad = useRef(true);
    const isEditing = editingTaskId !== null;

    const tableColumns = useMemo(() => {
        const raw = columns.length > 0
            ? columns
            : rows.length > 0
                ? normalizeColumns(Object.keys(rows[0] || {}))
                : [PRIMARY_KEY];
        return raw.filter((col) => !VISIBILITY_COLUMNS.has(col));
    }, [columns, rows]);

    const formColumns = useMemo(
        () => tableColumns.filter((column) => column !== PRIMARY_KEY && column !== CREATED_AT_COLUMN),
        [tableColumns]
    );

    const nonPrimaryColumns = useMemo(
        () => tableColumns.filter((col) => col !== PRIMARY_KEY),
        [tableColumns]
    );

    const buildFormRow = (source = {}) => {
        const allFormCols = [
            ...tableColumns.filter((c) => c !== PRIMARY_KEY && c !== CREATED_AT_COLUMN),
            IS_VISIBLE_BETA_COLUMN,
            IS_VISIBLE_PROD_COLUMN,
        ];
        const row = buildRowByColumns(allFormCols, source);
        if (!String(row[DATA_FORMAT_COLUMN] ?? '').trim()) {
            row[DATA_FORMAT_COLUMN] = DEFAULT_DATA_FORMAT;
        }
        if (row[IS_VISIBLE_BETA_COLUMN] === '') row[IS_VISIBLE_BETA_COLUMN] = 'true';
        if (row[IS_VISIBLE_PROD_COLUMN] === '') row[IS_VISIBLE_PROD_COLUMN] = 'true';
        return row;
    };

    const fetchRows = useCallback(async (page = 1) => {
        try {
            setLoading(true);
            setError('');
            const token = localStorage.getItem('prismax_admin_token');
            const response = await fetch(
                `${PRISMAX_BACKEND_URL}/api/vla-admin/tasks?page=${page}&page_size=${ITEMS_PER_PAGE}`,
                {headers: {'Authorization': `Bearer ${token}`}}
            );
            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(getApiErrorMessage(data, 'Failed to fetch VLA task data'));
            }
            const fetchedRows = Array.isArray(data.data) ? data.data : [];
            const metaColumns = normalizeColumns(data.meta?.columns || []);
            const metaColumnDetails = normalizeColumnDetails(data.meta?.column_details || {});
            const fallbackColumns = fetchedRows.length > 0
                ? normalizeColumns(Object.keys(fetchedRows[0] || {}))
                : [PRIMARY_KEY];
            setRows(fetchedRows);
            setColumns(metaColumns.length > 0 ? metaColumns : fallbackColumns);
            setColumnDetails(metaColumnDetails);
            setTotalPages(data.total_pages || 1);
        } catch (err) {
            if (isSessionExpiredError(err)) {
                handleAdminSessionExpired?.();
                return;
            }
            setError(err.message || 'Failed to fetch VLA task data');
        } finally {
            setLoading(false);
            isInitialLoad.current = false;
        }
    }, [handleAdminSessionExpired]);

    useEffect(() => {
        fetchRows(currentPage);
    }, [fetchRows, currentPage]);

    const handleOpenAdd = () => {
        setEditingTaskId(null);
        setSaveError('');
        setFormRow(buildFormRow());
        setPreviewVideoUploadId('');
        setShowModal(true);
    };

    const handleOpenEdit = (row) => {
        setEditingTaskId(row?.[PRIMARY_KEY] ?? null);
        setSaveError('');
        setFormRow(buildFormRow(row || {}));
        setPreviewVideoUploadId('');
        setShowModal(true);
    };

    const handleCloseModal = () => {
        if (isSaving) return;
        setShowModal(false);
        setEditingTaskId(null);
        setFormRow({});
        setPreviewVideoUploadId('');
        setSaveError('');
    };

    const handleInputChange = (column, value) => {
        setFormRow((prev) => ({...prev, [column]: value}));
    };

    const handleSubmit = async () => {
        setSaveError('');

        const allFormCols = [
            ...formColumns,
            IS_VISIBLE_BETA_COLUMN,
            IS_VISIBLE_PROD_COLUMN,
        ];

        for (const column of allFormCols) {
            if (VISIBILITY_COLUMNS.has(column)) continue;
            if (column === PREVIEW_VIDEO_URL_COLUMN && String(previewVideoUploadId || '').trim()) continue;
            const validationError = validateValueByType(column, formRow[column] ?? '', columnDetails[column]);
            if (validationError) {
                setSaveError(validationError);
                return;
            }
        }

        const rowPayload = {};
        allFormCols.forEach((column) => {
            const value = formRow[column] ?? '';
            if (!isEditing && String(value).trim() === '' && !columnDetails[column]?.required) return;
            rowPayload[column] = value;
        });
        if (String(previewVideoUploadId || '').trim()) {
            rowPayload[PREVIEW_VIDEO_UPLOAD_ID_FIELD] = String(previewVideoUploadId).trim();
        }

        try {
            setIsSaving(true);
            const token = localStorage.getItem('prismax_admin_token');
            const url = isEditing
                ? `${PRISMAX_BACKEND_URL}/api/vla-admin/tasks/${editingTaskId}`
                : `${PRISMAX_BACKEND_URL}/api/vla-admin/tasks`;
            const method = isEditing ? 'PATCH' : 'POST';
            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({row: rowPayload}),
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(getApiErrorMessage(data, `Failed to ${isEditing ? 'update' : 'create'} task`));
            }
            handleCloseModal();
            await fetchRows(currentPage);
        } catch (err) {
            if (isSessionExpiredError(err)) {
                handleAdminSessionExpired?.();
                return;
            }
            setSaveError(err.message || `Failed to ${isEditing ? 'update' : 'create'} task`);
        } finally {
            setIsSaving(false);
        }
    };

    const handleToggleVisibility = async (row, field) => {
        const taskId = row[PRIMARY_KEY];
        const currentValue = row[field];
        const newValue = !currentValue;
        const toggleKey = `${taskId}-${field}`;

        setTogglingId(toggleKey);
        setRows((prev) =>
            prev.map((r) => r[PRIMARY_KEY] === taskId ? {...r, [field]: newValue} : r)
        );

        try {
            const token = localStorage.getItem('prismax_admin_token');
            const response = await fetch(
                `${PRISMAX_BACKEND_URL}/api/vla-admin/tasks/${taskId}/set-visibility`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({[field]: newValue}),
                }
            );
            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(getApiErrorMessage(data, 'Failed to update visibility'));
            }
        } catch (err) {
            setRows((prev) =>
                prev.map((r) => r[PRIMARY_KEY] === taskId ? {...r, [field]: currentValue} : r)
            );
            if (isSessionExpiredError(err)) handleAdminSessionExpired?.();
        } finally {
            setTogglingId(null);
        }
    };

    const paginate = (page) => setCurrentPage(page);
    const goToPreviousPage = () => { if (currentPage > 1) setCurrentPage((p) => p - 1); };
    const goToNextPage = () => { if (currentPage < totalPages) setCurrentPage((p) => p + 1); };

    if (loading && isInitialLoad.current) {
        return <div className={styles.loading}>Loading VLA task data...</div>;
    }

    if (error) {
        return <div className={styles.error}>Error: {error}</div>;
    }

    const totalColumnCount = tableColumns.length + 2; // +Visibility +Actions

    return (
        <div className={styles.tableContainer}>
            <div className={styles.tableHeader}>
                <h2 className={styles.tableTitle}>VLA Admin - Tasks</h2>
                <button className={styles.addButton} onClick={handleOpenAdd}>
                    Add Row
                </button>
            </div>

            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>{toLabel(PRIMARY_KEY)}</th>
                            <th>Visibility</th>
                            {nonPrimaryColumns.map((column) => (
                                <th
                                    key={column}
                                    className={isExpandedColumn(column) ? styles.taskExpandedColumn : undefined}
                                    title={toLabel(column)}
                                >
                                    {toLabel(column)}
                                </th>
                            ))}
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length > 0 ? rows.map((row, index) => (
                            <tr key={`${row[PRIMARY_KEY] ?? 'row'}-${index}`}>
                                <td>{row[PRIMARY_KEY] ?? '-'}</td>
                                <td>
                                    <div className={styles.taskVisibilityButtons}>
                                        <button
                                            className={row[IS_VISIBLE_BETA_COLUMN] ? styles.editButton : styles.deleteButton}
                                            disabled={togglingId === `${row[PRIMARY_KEY]}-${IS_VISIBLE_BETA_COLUMN}`}
                                            onClick={() => handleToggleVisibility(row, IS_VISIBLE_BETA_COLUMN)}
                                        >
                                            {togglingId === `${row[PRIMARY_KEY]}-${IS_VISIBLE_BETA_COLUMN}`
                                                ? '...'
                                                : row[IS_VISIBLE_BETA_COLUMN] ? 'Beta On' : 'Beta Off'}
                                        </button>
                                        <button
                                            className={row[IS_VISIBLE_PROD_COLUMN] ? styles.editButton : styles.deleteButton}
                                            disabled={togglingId === `${row[PRIMARY_KEY]}-${IS_VISIBLE_PROD_COLUMN}`}
                                            onClick={() => handleToggleVisibility(row, IS_VISIBLE_PROD_COLUMN)}
                                        >
                                            {togglingId === `${row[PRIMARY_KEY]}-${IS_VISIBLE_PROD_COLUMN}`
                                                ? '...'
                                                : row[IS_VISIBLE_PROD_COLUMN] ? 'Prod On' : 'Prod Off'}
                                        </button>
                                    </div>
                                </td>
                                {nonPrimaryColumns.map((column) => {
                                    const value = row[column];
                                    if (isExpandedColumn(column)) {
                                        return (
                                            <td key={`${row[PRIMARY_KEY] ?? index}-${column}`} className={styles.taskExpandedColumn}>
                                                {renderExpandedCell(column, value)}
                                            </td>
                                        );
                                    }
                                    const displayValue = value === null || value === undefined || value === '' ? '-' : String(value);
                                    return (
                                        <td key={`${row[PRIMARY_KEY] ?? index}-${column}`}>
                                            <span title={displayValue}>{displayValue}</span>
                                        </td>
                                    );
                                })}
                                <td>
                                    <button className={styles.editButton} onClick={() => handleOpenEdit(row)}>
                                        Edit
                                    </button>
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan={totalColumnCount} className={styles.noData}>
                                    No VLA tasks found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className={styles.pagination}>
                    <button
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                        className={`${styles.pageButton} ${currentPage === 1 ? styles.disabled : ''}`}
                    >
                        Previous
                    </button>
                    <div className={styles.pageNumbers}>
                        {Array.from({length: totalPages}, (_, i) => i + 1).map((number) => {
                            if (
                                number === 1 ||
                                number === totalPages ||
                                (number >= currentPage - 2 && number <= currentPage + 2)
                            ) {
                                return (
                                    <button
                                        key={number}
                                        onClick={() => paginate(number)}
                                        className={`${styles.pageNumber} ${currentPage === number ? styles.active : ''}`}
                                    >
                                        {number}
                                    </button>
                                );
                            } else if (
                                (number === currentPage - 3 && currentPage > 4) ||
                                (number === currentPage + 3 && currentPage < totalPages - 3)
                            ) {
                                return <span key={number} className={styles.ellipsis}>...</span>;
                            }
                            return null;
                        })}
                    </div>
                    <button
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                        className={`${styles.pageButton} ${currentPage === totalPages ? styles.disabled : ''}`}
                    >
                        Next
                    </button>
                </div>
            )}

            {showModal && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modal}>
                        <h3>{isEditing ? 'Edit Task' : 'Add Task'}</h3>
                        <p className={styles.modalDescription}>
                            Task ID is managed by the database and cannot be edited here. Created At is also auto-generated.
                        </p>
                        {isEditing && (
                            <div className={styles.fieldHint}>
                                Task ID: {String(editingTaskId)}
                            </div>
                        )}

                        {formColumns.map((column) => {
                            const detail = columnDetails[column];
                            if (column === PREVIEW_VIDEO_URL_COLUMN) {
                                return (
                                    <div className={styles.formGroup} key={column}>
                                        <label>Preview Video Upload ID:</label>
                                        <input
                                            type="text"
                                            value={previewVideoUploadId}
                                            onChange={(event) => setPreviewVideoUploadId(event.target.value)}
                                            placeholder="Optional . Type: Integer"
                                            className={styles.input}
                                            inputMode="numeric"
                                        />
                                    </div>
                                );
                            }
                            const commonProps = {
                                value: formRow[column] ?? '',
                                onChange: (event) => handleInputChange(column, event.target.value),
                                placeholder: getColumnHint(column, detail),
                                className: shouldUseTextarea(column) ? `${styles.input} ${styles.textarea}` : styles.input,
                            };
                            return (
                                <div className={styles.formGroup} key={column}>
                                    <label>{toLabel(column)}:</label>
                                    {shouldUseTextarea(column) ? (
                                        <textarea {...commonProps} rows={4} />
                                    ) : (
                                        <input type="text" {...commonProps} inputMode={getInputMode(detail)} />
                                    )}
                                    <div className={styles.fieldHint}>
                                        {getColumnHint(column, detail)}
                                    </div>
                                </div>
                            );
                        })}

                        <div className={styles.formGroup}>
                            <label>Is Visible Beta:</label>
                            <input
                                type="text"
                                value={formRow[IS_VISIBLE_BETA_COLUMN] ?? 'true'}
                                onChange={(e) => handleInputChange(IS_VISIBLE_BETA_COLUMN, e.target.value)}
                                placeholder="true or false"
                                className={styles.input}
                            />
                            <div className={styles.fieldHint}>Optional • Type: Boolean</div>
                        </div>

                        <div className={styles.formGroup}>
                            <label>Is Visible Production:</label>
                            <input
                                type="text"
                                value={formRow[IS_VISIBLE_PROD_COLUMN] ?? 'true'}
                                onChange={(e) => handleInputChange(IS_VISIBLE_PROD_COLUMN, e.target.value)}
                                placeholder="true or false"
                                className={styles.input}
                            />
                            <div className={styles.fieldHint}>Optional • Type: Boolean</div>
                        </div>

                        {saveError && <div className={styles.errorMessage}>{saveError}</div>}

                        <div className={styles.modalButtons}>
                            <button
                                className={styles.cancelButton}
                                onClick={handleCloseModal}
                                disabled={isSaving}
                            >
                                Cancel
                            </button>
                            <button
                                className={styles.cancelButton}
                                onClick={handleSubmit}
                                disabled={isSaving}
                            >
                                {isSaving ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VLAAdminTasksTable;
